<template>
  <div
    :class="[
      `flex min-w-0 grow select-none items-center text-sm`,
      !hasValue && `text-muted-foreground`,
    ]"
    @click="handleContainerClick"
    @focusout="handleFocusOut"
  >
    <template v-for="(part, index) in renderedParts" :key="index">
      <span v-if="part.kind === 'literal'" class="px-1 text-muted-foreground">
        {{ part.value }}
      </span>
      <DateSegment
        v-else
        ref="inputs"
        :segment="part.data"
        :disabled="disabled"
        @change="part.onChange"
        @increment="part.onIncrement"
        @next="part.onNext"
        @prev="part.onPrev"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, useTemplateRef, watch } from "vue";

import { mapDefined } from "@/lib/collections";
import { getLocaleDateParts, isValidISODate } from "@/lib/time";

import DateSegment, { type SegmentData } from "./DateSegment.vue";

type SegmentType = "day" | "month" | "year";

interface FormatPart {
  type: "day" | "literal" | "month" | "year";
  value: string;
}

const modelValue = defineModel<string>({ default: "" });

defineProps<{
  disabled?: boolean;
}>();

const emit = defineEmits<{
  focusAfterLast: [];
}>();

const inputs = useTemplateRef("inputs");

const segments = ref<{
  day: number | null;
  month: number | null;
  year: number | null;
}>({
  day: null,
  month: null,
  year: null,
});

const hasValue = computed(() => {
  return (
    segments.value.day !== null &&
    segments.value.month !== null &&
    segments.value.year !== null
  );
});

const formatParts = computed((): FormatPart[] => {
  return getLocaleDateParts() as FormatPart[];
});

const segmentOrder = computed((): SegmentType[] => {
  return mapDefined(formatParts.value, (p) => {
    if (p.type === "day" || p.type === "month" || p.type === "year") {
      return p.type;
    }
  });
});

interface RenderedSegment {
  kind: "segment";
  type: SegmentType;
  data: SegmentData;
  onChange: (value: number | null) => void;
  onIncrement: (delta: number) => void;
  onNext: () => void;
  onPrev: () => void;
}

interface RenderedLiteral {
  kind: "literal";
  value: string;
}

type RenderedPart = RenderedSegment | RenderedLiteral;

const renderedParts = computed((): RenderedPart[] => {
  return formatParts.value.map((part) => {
    if (part.type === "literal") {
      return { kind: "literal", value: part.value };
    }

    const type = part.type;

    return {
      kind: "segment",
      type: type,
      data: getSegmentData(type),
      onChange: (value) => handleSegmentChange(type, value),
      onIncrement: (delta) => handleSegmentIncrement(type, delta),
      onNext: () => focusNextFrom(type),
      onPrev: () => focusPrevFrom(type),
    };
  });
});

const daysInMonth = (year: number, month: number): number => {
  return new Date(year, month, 0).getDate();
};

const getSegmentData = (type: SegmentType): SegmentData => {
  const value = segments.value[type];

  // Use fixed max values to allow temporarily invalid values during editing
  switch (type) {
    case "day": {
      return { type, value, min: 1, max: 31 };
    }
    case "month": {
      return { type, value, min: 1, max: 12 };
    }
    case "year": {
      return { type, value, min: 1, max: 9999 };
    }
  }
};

const getSegmentIndex = (type: SegmentType): number => {
  return segmentOrder.value.indexOf(type);
};

const focusSegmentByIndex = (index: number): void => {
  inputs.value?.[index]?.focus();
};

const focusSegment = (type: SegmentType): void => {
  const index = getSegmentIndex(type);
  if (index !== -1) {
    focusSegmentByIndex(index);
  }
};

const focusFirstSegment = (): void => {
  const firstSegment = segmentOrder.value[0];
  if (firstSegment) {
    focusSegment(firstSegment);
  }
};

const focusLastSegment = (): void => {
  const lastSegment = segmentOrder.value[segmentOrder.value.length - 1];
  if (lastSegment) {
    focusSegment(lastSegment);
  }
};

const focusNextFrom = (type: SegmentType): void => {
  const order = segmentOrder.value;
  const currentIndex = order.indexOf(type);
  if (currentIndex < order.length - 1) {
    focusSegment(order[currentIndex + 1]);
  } else {
    emit("focusAfterLast");
  }
};

const focusPrevFrom = (type: SegmentType): void => {
  const order = segmentOrder.value;
  const currentIndex = order.indexOf(type);
  if (currentIndex > 0) {
    focusSegment(order[currentIndex - 1]);
  }
};

const handleContainerClick = (event: MouseEvent): void => {
  const target = event.target as Element;
  if (target.closest("[data-segment]")) return;

  focusFirstSegment();
};

const handleFocusOut = (event: FocusEvent): void => {
  const container = event.currentTarget as Element;
  const relatedTarget = event.relatedTarget as Element | null;

  // Only commit when focus leaves the entire component
  if (relatedTarget && container.contains(relatedTarget)) {
    return;
  }

  commitValue();
};

const commitValue = (): void => {
  clampSegments();
  updateModelValue(true);
};

const handleSegmentChange = (type: SegmentType, value: number | null): void => {
  segments.value[type] = value;
  updateModelValue();
};

const handleSegmentIncrement = (type: SegmentType, delta: number): void => {
  const current = segments.value[type];

  // Use fixed max (31) for day to allow temporarily invalid values during editing
  // Values are clamped to valid range on blur
  switch (type) {
    case "day": {
      const max = 31;
      if (current === null) {
        segments.value.day = delta > 0 ? 1 : max;
      } else {
        let next = current + delta;
        if (next < 1) next = max;
        if (next > max) next = 1;
        segments.value.day = next;
      }
      break;
    }
    case "month": {
      if (current === null) {
        segments.value.month = delta > 0 ? 1 : 12;
      } else {
        let next = current + delta;
        if (next < 1) next = 12;
        if (next > 12) next = 1;
        segments.value.month = next;
      }
      break;
    }
    case "year": {
      const thisYear = new Date().getFullYear();
      if (current === null) {
        segments.value.year = thisYear;
      } else {
        segments.value.year = current + delta;
      }
      break;
    }
  }

  updateModelValue();
};

const clampSegments = (): void => {
  const { day, month, year } = segments.value;

  if (month !== null) {
    if (month < 1) {
      segments.value.month = 1;
    }
    if (month > 12) {
      segments.value.month = 12;
    }
  }

  if (day !== null) {
    if (day < 1) {
      segments.value.day = 1;
    } else if (month !== null && year !== null) {
      const maxDay = daysInMonth(year, month);
      if (day > maxDay) {
        segments.value.day = maxDay;
      }
    }
  }
};

const parseISODate = (
  iso: string
): { year: number; month: number; day: number } | null => {
  if (!isValidISODate(iso)) return null;
  const [year, month, day] = iso.split("-").map(Number);
  return { year, month, day };
};

const toISODate = (year: number, month: number, day: number): string => {
  return `${year.toString().padStart(4, "0")}-${month
    .toString()
    .padStart(2, "0")}-${day.toString().padStart(2, "0")}`;
};

const isValidDate = (year: number, month: number, day: number): boolean => {
  const date = new Date(year, month - 1, day);
  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
};

let snapshot: string | undefined;

const updateModelValue = (force = false): void => {
  const { day, month, year } = segments.value;

  if (day === null && month === null && year === null) {
    snapshot = "";
    modelValue.value = "";
    return;
  }

  if (day === null || month === null || year === null) {
    return;
  }

  if (!isValidDate(year, month, day)) {
    if (force) {
      snapshot = "";
      modelValue.value = "";
    }
    return;
  }

  const next = toISODate(year, month, day);
  if (next !== modelValue.value) {
    snapshot = next;
    modelValue.value = next;
  }
};

watch(
  modelValue,
  (value) => {
    if (snapshot === value) {
      return;
    }

    snapshot = value;

    if (!value) {
      segments.value = { day: null, month: null, year: null };
      return;
    }

    const parsed = parseISODate(value);
    if (parsed) {
      segments.value = {
        day: parsed.day,
        month: parsed.month,
        year: parsed.year,
      };
    }
  },
  { immediate: true }
);

defineExpose({
  focusFirstSegment,
  focusLastSegment,
  clampSegments,
  updateModelValue,
});
</script>
